import React, { useEffect, useMemo, useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import ButtonBase from "@mui/material/ButtonBase";
import Chip from "@mui/material/Chip";
import LinearProgress from "@mui/material/LinearProgress";
import Paper from "@mui/material/Paper";
import Skeleton from "@mui/material/Skeleton";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import BadgeRoundedIcon from "@mui/icons-material/BadgeRounded";
import BusinessRoundedIcon from "@mui/icons-material/BusinessRounded";
import GroupRoundedIcon from "@mui/icons-material/GroupRounded";
import TimerRoundedIcon from "@mui/icons-material/TimerRounded";
import { getAllOrganisations, getAllUsers } from "../../actions";
import { canPlatform, platformRoleLabel } from "../../utils/permissions";
import { brand } from "../../theme/businessFlowsTheme";
import { Caution, Surface, focusRing } from "../workspaces/WorkspaceUi";

const PLAN_LABELS = { trial: "Trial", ultimate: "Ultimate", ultimatePlus: "Ultimate Plus", expired: "Expired" };
const DAY = 24 * 60 * 60 * 1000;

const daysLeft = (date) => {
  if (!date) return null;
  const ms = new Date(date).getTime() - Date.now();
  return Number.isNaN(ms) ? null : Math.ceil(ms / DAY);
};

function Metric({ icon, label, value, hint, onClick }) {
  const content = (
    <>
      <Stack direction="row" spacing={1.25} alignItems="center">
        <Box sx={{ width: 34, height: 34, borderRadius: 2.5, display: "grid", placeItems: "center", bgcolor: brand.mark, color: brand.ink }}>
          {icon}
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
          {label}
        </Typography>
      </Stack>
      <Typography sx={{ fontWeight: 700, fontSize: "1.9rem", lineHeight: 1.1, mt: 1.5 }}>{value}</Typography>
      {hint && (
        <Typography variant="caption" color="text.secondary">
          {hint}
        </Typography>
      )}
    </>
  );
  const sx = {
    p: 2,
    borderRadius: 4,
    border: `1px solid ${brand.line}`,
    textAlign: "left",
    alignItems: "stretch",
    flexDirection: "column",
    display: "flex",
    width: "100%",
    bgcolor: "#fff",
  };
  return onClick ? (
    <ButtonBase
      onClick={onClick}
      sx={{
        ...sx,
        "&:hover": { borderColor: brand.ink },
        "&:focus-visible": { outline: `2px solid ${brand.ink}`, outlineOffset: 2 },
      }}
    >
      {content}
    </ButtonBase>
  ) : (
    <Paper elevation={0} sx={sx}>
      {content}
    </Paper>
  );
}

export default function AdminOverview({ user, onOpen }) {
  const [users, setUsers] = useState(null);
  const [organisations, setOrganisations] = useState(null);
  const [error, setError] = useState("");

  const maySeeUsers = canPlatform(user, "users.view");
  const maySeeTenants = canPlatform(user, "tenants.view");

  useEffect(() => {
    let active = true;
    Promise.all([maySeeUsers ? getAllUsers() : null, maySeeTenants ? getAllOrganisations() : null]).then(([usersRes, orgRes]) => {
      if (!active) return;
      if (usersRes && !usersRes.success) setError("Some data couldn't be loaded. Refresh to try again.");
      if (orgRes && !orgRes.success) setError("Some data couldn't be loaded. Refresh to try again.");
      setUsers(usersRes?.success ? usersRes.content : []);
      setOrganisations(orgRes?.success ? orgRes.content : []);
    });
    return () => {
      active = false;
    };
  }, [maySeeUsers, maySeeTenants]);

  const stats = useMemo(() => {
    const list = users || [];
    const orgs = organisations || [];
    const staff = list.filter((u) => u.effectivePlatformRole);
    const expiring = list.filter((u) => {
      const left = daysLeft(u.expirationDate);
      return u.expired !== true && left !== null && left >= 0 && left <= 14;
    });
    const byPlan = {};
    orgs.forEach((org) => {
      const plan = org.subscription?.plan || "none";
      byPlan[plan] = (byPlan[plan] || 0) + 1;
    });
    const recent = [...list]
      .filter((u) => u.registrationDate)
      .sort((a, b) => new Date(b.registrationDate) - new Date(a.registrationDate))
      .slice(0, 5);
    return {
      users: list.length,
      staff,
      expiring,
      organisations: orgs.length,
      subscribed: orgs.filter((o) => o.subscription?.plan).length,
      byPlan,
      recent,
    };
  }, [users, organisations]);

  const loading = (maySeeUsers && users === null) || (maySeeTenants && organisations === null);

  if (loading) {
    return (
      <Box sx={{ p: { xs: 2, md: 3 } }}>
        <Stack direction="row" spacing={2} sx={{ flexWrap: "wrap", rowGap: 2 }}>
          {[0, 1, 2, 3].map((i) => (
            <Skeleton key={i} variant="rounded" height={128} sx={{ flex: "1 1 200px", borderRadius: 4 }} />
          ))}
        </Stack>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, ...focusRing }}>
      <Typography variant="h5" component="h1" sx={{ fontWeight: 700 }}>
        Overview
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 3 }}>
        Signed in as {platformRoleLabel(user.platformRole)}. Everything here follows your permissions.
      </Typography>

      {error && (
        <Caution sx={{ mb: 2 }} next="The numbers below leave out whatever didn't load.">
          {error}
        </Caution>
      )}

      <Box sx={{ display: "grid", gap: 2, gridTemplateColumns: "repeat(auto-fit, minmax(min(210px, 100%), 1fr))" }}>
        {maySeeUsers && (
          <Metric icon={<GroupRoundedIcon fontSize="small" />} label="Active users" value={stats.users} hint="Not archived" onClick={() => onOpen("users")} />
        )}
        {maySeeUsers && (
          <Metric
            icon={<BadgeRoundedIcon fontSize="small" />}
            label="bpExperts staff"
            value={stats.staff.length}
            hint={stats.staff.map((s) => platformRoleLabel(s.effectivePlatformRole)).filter((v, i, a) => a.indexOf(v) === i).join(", ") || "None yet"}
            onClick={() => onOpen("users")}
          />
        )}
        {maySeeTenants && (
          <Metric
            icon={<BusinessRoundedIcon fontSize="small" />}
            label="Workspaces"
            value={stats.organisations}
            hint={`${stats.subscribed} with a subscription`}
            onClick={() => onOpen("workspaces")}
          />
        )}
        {maySeeUsers && (
          <Metric
            icon={<TimerRoundedIcon fontSize="small" />}
            label="Expiring in 14 days"
            value={stats.expiring.length}
            hint="Personal trial dates"
            onClick={() => onOpen("users")}
          />
        )}
      </Box>

      <Box sx={{ display: "grid", gap: 2, mt: 2, gridTemplateColumns: { xs: "1fr", lg: "1fr 1fr" } }}>
        {maySeeTenants && (
          <Surface sx={{ p: 2.5 }}>
            <Typography component="h2" sx={{ fontWeight: 700, mb: 2 }}>
              Workspaces by plan
            </Typography>
            <Stack spacing={1.5}>
              {["ultimatePlus", "ultimate", "trial", "none"].map((plan) => {
                const count = stats.byPlan[plan] || 0;
                const share = stats.organisations ? (count / stats.organisations) * 100 : 0;
                return (
                  <Box key={plan}>
                    <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {PLAN_LABELS[plan] || "No subscription"}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {count}
                      </Typography>
                    </Stack>
                    <LinearProgress
                      variant="determinate"
                      value={share}
                      sx={{ height: 8, borderRadius: 999, bgcolor: brand.canvas, "& .MuiLinearProgress-bar": { bgcolor: plan === "none" ? brand.muted : brand.ink } }}
                    />
                  </Box>
                );
              })}
            </Stack>
            <Button size="small" sx={{ mt: 2 }} onClick={() => onOpen("workspaces")}>
              Manage workspaces
            </Button>
          </Surface>
        )}

        {maySeeUsers && (
          <Surface sx={{ p: 2.5 }}>
            <Typography component="h2" sx={{ fontWeight: 700, mb: 2 }}>
              Newest accounts
            </Typography>
            <Stack spacing={1.25}>
              {stats.recent.length === 0 && (
                <Typography variant="body2" color="text.secondary">
                  No accounts yet. Invite someone into a workspace and they appear here.
                </Typography>
              )}
              {stats.recent.map((account) => (
                <Stack key={account.id} direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, overflowWrap: "anywhere" }}>
                      {[account.firstName, account.lastName].filter(Boolean).join(" ") || account.email}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ overflowWrap: "anywhere" }}>
                      {account.company || account.email}
                    </Typography>
                  </Box>
                  <Chip
                    size="small"
                    label={PLAN_LABELS[account.subscription] || account.subscription || "No plan"}
                    sx={{ flexShrink: 0, bgcolor: brand.shell, color: brand.muted }}
                  />
                </Stack>
              ))}
            </Stack>
            <Button size="small" sx={{ mt: 2 }} onClick={() => onOpen("users")}>
              Manage users
            </Button>
          </Surface>
        )}
      </Box>
    </Box>
  );
}
